/**
 * ON-SITE SALES REPORT
 *
 * Standalone product sales made by scanning a printed QR code. These are NOT event bookings
 * and are deliberately reported separately — see ONSITE_PRODUCTS_DESIGN.md §8.
 *
 * Stripe is the source of truth. Rows arrive here two ways: a webhook writes them live as
 * sales complete, and "Sync Stripe payments" pulls current state to backfill anything missed
 * AND refresh refund status. Only the sync can see a refund issued in the Stripe dashboard,
 * which is why lastSyncedAt is shown — an organizer should not settle with a vendor using
 * figures that predate a refund.
 *
 * Rendered as a tab panel inside ProductsPage, so Sync and Export sit in an inline toolbar
 * rather than the layout header.
 */
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import moment from "moment";

import noEventImage from "../../assets/noEventImage.svg";
import {
  TwButton,
  TwDateRangePicker,
  TwListMoreButton,
  TwMoreOptions,
  TwSelectGroup,
} from "../../components/tailwind";
import OnsiteSaleDetailsDrawer from "./OnsiteSaleDetailsDrawer";
import {
  clearLoadingData,
  loadingState,
  setSectionLoading,
} from "../../redux/reducers/loadingReducer";
import { listOnsiteSales, syncOnsiteSales, listProducts } from "../../utils/productsApi";

const STATUS_LABELS = {
  paid: "Paid",
  refunded: "Refunded",
  partially_refunded: "Partially refunded",
  disputed: "Disputed",
};

const eur = (amount) => `€${parseFloat(amount || 0).toFixed(2)}`;

/**
 * Quantity already has its own column, so strip any trailing " x N".
 * Done here as well as server-side: rows written before the fix have the suffix baked into
 * productSnapshot.name, and this makes them display correctly without needing a re-sync.
 */
const productName = (sale) =>
  String(sale?.productSnapshot?.name || "").replace(/\s+x\s*\d+\s*$/i, "").trim();

/** How many item lines to name in the table before collapsing the rest into "+N more". */
const ITEMS_SHOWN_IN_TABLE = 2;

/**
 * What the Product column shows.
 *
 * A basket has several items, so the snapshot name is only a count ("3 items") — useless for
 * telling two orders apart at a glance. Where the line breakdown exists, the first couple of
 * items are named and the remainder collapsed; the drawer lists all of them.
 *
 * Single-item sales are unchanged.
 */
const itemLines = (sale) => (Array.isArray(sale?.itemsBought) ? sale.itemsBought : []);

/**
 * "Beer x 2" — the quantity is always shown, including for a single unit.
 *
 * Consistency beats brevity in a column someone scans down: "Beer" and "Beer x 2" sitting in
 * the same column invites reading the first as an unknown quantity.
 */
const describeItem = (item) => {
  const name = String(item?.name || item?.productId || "").trim() || "Item";
  const quantity = parseInt(item?.quantity || 1);
  return `${name} x ${quantity}`;
};

const productSummary = (sale) => {
  const items = itemLines(sale);

  // Rows written before baskets existed have no itemsBought — rebuild the same
  // "name x qty" shape from the snapshot so the column reads consistently throughout.
  if (items.length === 0) {
    const name = productName(sale);
    return name ? `${name} x ${parseInt(sale?.quantity || 1)}` : "—";
  }
  if (items.length === 1) return describeItem(items[0]);

  const shown = items.slice(0, ITEMS_SHOWN_IN_TABLE).map(describeItem).join(", ");
  const remaining = items.length - ITEMS_SHOWN_IN_TABLE;
  return remaining > 0 ? `${shown} +${remaining} more` : shown;
};

/** Inline SVG rather than a heroicons import, so this can't break on an icon rename. */
const InfoIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="size-4 text-gray-400" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 9a1 1 0 011-1h.01a1 1 0 01.99 1v4a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z"
      clipRule="evenodd"
    />
  </svg>
);

/**
 * Payout cell. The figure is the connected account's balance-transaction net — what actually
 * lands in the organizer's Stripe balance, already net of Stripe's fee and our platform fee.
 * The tooltip shows the arithmetic so the number is checkable rather than asserted.
 */
const PayoutCell = ({ sale }) => {
  const hasPayout = Number(sale?.netPayoutAmount || 0) > 0;

  // A native title attribute rather than a styled tooltip: the table wrapper uses
  // overflow-x-auto, and CSS computes the other axis to auto as well, which clips any
  // absolutely-positioned tooltip. The browser's own tooltip is never clipped.
  const breakdown = hasPayout
    ? [
        `Gross paid: ${eur(sale.totalAmount)}`,
        `Stripe fee: -${eur(sale.stripeFeeAmount)}`,
        `Platform fee: -${eur(sale.platformFeeAmount)}`,
        `Expected payout: ${eur(sale.netPayoutAmount)}`,
      ].join("\n")
    : "Fee details arrive from Stripe shortly after payment. Press Sync Stripe payments to fill them in.";

  return (
    <span
      title={breakdown}
      className="inline-flex items-center gap-1 cursor-help"

    >
      {hasPayout ? (
        <span className="tabular-nums">{eur(sale.netPayoutAmount)}</span>
      ) : (
        <span className="text-gray-400">—</span>
      )}
      <InfoIcon />
    </span>
  );
};

const statusClass = (status) =>
  status === "refunded" || status === "disputed"
    ? "text-red-600"
    : status === "partially_refunded"
    ? "text-amber-600"
    : "text-gray-700";

export default function OnsiteSalesList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loadingReduxState = useSelector(loadingState);

  const [sales, setSales] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [lastSyncedAt, setLastSyncedAt] = React.useState(null);
  const [products, setProducts] = React.useState([]);

  const [productFilter, setProductFilter] = React.useState("");
  const [sortBy, setSortBy] = React.useState("soldAt");
  const [dateRange, setDateRange] = React.useState({ from: null, to: null });

  const toIsoDay = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");
  const fromDate = toIsoDay(dateRange.from);
  const toDate = toIsoDay(dateRange.to);

  const [detailsSale, setDetailsSale] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [message, setMessage] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    dispatch(setSectionLoading(true));

    const result = await listOnsiteSales({
      ...(productFilter ? { productId: productFilter } : {}),
      ...(fromDate ? { from: fromDate } : {}),
      // Include the whole end day, not just its midnight boundary.
      ...(toDate ? { to: `${toDate}T23:59:59.999Z` } : {}),
    });
    if (!result.error) {
      setSales(Array.isArray(result.data?.sales) ? result.data.sales : []);
      setSummary(result.data?.summary || null);
      setLastSyncedAt(result.data?.lastSyncedAt || null);
    }

    dispatch(clearLoadingData());
    setLoading(false);
  }, [dispatch, productFilter, fromDate, toDate]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    (async () => {
      const result = await listProducts({ includeInactive: true });
      if (!result.error) setProducts(Array.isArray(result.data) ? result.data : []);
    })();
  }, []);

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setMessage(
      t(
        "onsiteSales.syncing",
        "Syncing with Stripe — this may take a couple of minutes to reflect the most recent payments."
      )
    );

    const result = await syncOnsiteSales({ days: 90 });
    setSyncing(false);

    if (result.error) {
      setMessage(result.message);
      return;
    }
    setMessage(
      t("onsiteSales.syncDone", "Sync complete — {{count}} payments checked.", {
        count: result.data?.synced ?? 0,
      })
    );
    load();
  };

  const sorted = React.useMemo(() => {
    const rows = [...sales];
    rows.sort((a, b) => {
      if (sortBy === "product") {
        return productName(a).localeCompare(productName(b));
      }
      if (sortBy === "amount") return (b.totalAmount || 0) - (a.totalAmount || 0);
      return new Date(b.soldAt || 0) - new Date(a.soldAt || 0);
    });
    return rows;
  }, [sales, sortBy]);

  const handleExportExcel = () => {
    const data = sorted.map((sale) => ({
      Date: sale.soldAt ? moment(sale.soldAt).format("YYYY-MM-DD HH:mm") : "",
      // The export gets the FULL basket, never "+N more" — a spreadsheet is where someone
      // reconciles, and a truncated line would be useless there.
      "Items purchased": itemLines(sale).length
        ? itemLines(sale).map(describeItem).join(", ")
        : productName(sale),
      Quantity: sale.quantity || 0,
      "Unit price (incl. VAT)": sale.unitAmount || 0,
      "Total (incl. VAT)": sale.totalAmount || 0,
      "Stripe fee": sale.stripeFeeAmount || 0,
      "Platform fee": sale.platformFeeAmount || 0,
      "Your payout": sale.netPayoutAmount || 0,
      Status: STATUS_LABELS[sale.paymentStatus] || sale.paymentStatus || "",
      Reference: sale.saleReference || "",
      "Stripe payment": sale.stripePaymentIntentId || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "On-site sales");
    XLSX.writeFile(workbook, `onsite-sales-${moment().format("YYYY-MM-DD")}.xlsx`);
  };

  const isEmpty =
    sales.length === 0 && loading === false && loadingReduxState.sectionLoading === false;

  return (
    <div>
      <div className="flex flex-wrap justify-end gap-3 mb-5">
        <TwButton
          className="sm:min-w-32"
          onClick={handleSync}
          actionLoading={syncing}
          disabled={syncing}
        >
          {t("onsiteSales.sync", "Sync Stripe payments")}
        </TwButton>
        <TwButton
          variant="primary-outline"
          className="sm:min-w-32"
          disabled={sales.length === 0}
          onClick={handleExportExcel}
        >
          {t("myEvents.exportToExcel", "Export to Excel")}
        </TwButton>
      </div>

          <p className="text-sm text-gray-500 mb-2 max-w-2xl">
            {t(
              "onsiteSales.intro",
              "Items sold by scanning a printed QR code at your venue. These are separate from event ticket bookings."
            )}
          </p>

          <p className="text-xs text-gray-400 mb-5">
            {lastSyncedAt
              ? t("onsiteSales.lastSynced", "Last synced {{when}}", {
                  when: moment(lastSyncedAt).format("D MMM YYYY, HH:mm"),
                })
              : t("onsiteSales.neverSynced", "Not synced yet — refunds may not be reflected.")}
          </p>

          {message ? (
            <p className="text-sm text-gray-600 mb-4">{message}</p>
          ) : null}

          {summary ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {[
                [t("onsiteSales.transactions", "Transactions"), summary.transactionCount],
                [t("onsiteSales.itemsSold", "Items sold"), summary.itemsSold],
                [t("onsiteSales.gross", "Gross (incl. VAT)"), eur(summary.grossAmount)],
                [t("onsiteSales.payout", "Your payout"), eur(summary.netPayoutAmount)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="text-xl font-semibold mt-1">{value}</p>
                </div>
              ))}
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 items-end">
            <TwSelectGroup
              label={t("onsiteSales.filterProduct", "Product")}
              name="productFilter"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              options={[
                { value: "", title: t("onsiteSales.allProducts", "All products") },
                ...products.map((p) => ({ value: p._id, title: p.name })),
              ]}
            />

            <TwDateRangePicker
              label={t("onsiteSales.dateRange", "Date range")}
              placeholder={t("onsiteSales.allDates", "All dates")}
              value={dateRange}
              onChange={setDateRange}
            />

            <TwSelectGroup
              label={t("onsiteSales.sortBy", "Sort by")}
              name="sortBy"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              options={[
                { value: "soldAt", title: t("onsiteSales.sortDate", "Newest first") },
                { value: "product", title: t("onsiteSales.sortProduct", "Product name") },
                { value: "amount", title: t("onsiteSales.sortAmount", "Amount") },
              ]}
            />
          </div>

          {sorted.length ? (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left font-medium px-4 py-3">{t("onsiteSales.date", "Date")}</th>
                    <th className="text-left font-medium px-4 py-3">
                      {t("onsiteSales.itemsPurchased", "Items purchased")}
                    </th>
                    <th className="text-right font-medium px-4 py-3">{t("onsiteSales.qty", "Qty")}</th>
                    <th className="text-right font-medium px-4 py-3">{t("onsiteSales.total", "Total")}</th>
                    <th className="text-right font-medium px-4 py-3">{t("onsiteSales.payout", "Your payout")}</th>
                    <th className="text-left font-medium px-4 py-3">{t("onsiteSales.status", "Status")}</th>
                    <th className="relative px-4 py-3"><span className="sr-only">{t("account.tableActionHead", "Actions")}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((sale) => (
                    <tr key={sale._id} className="border-t border-gray-100">
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {sale.soldAt ? moment(sale.soldAt).format("D MMM, HH:mm") : "—"}
                      </td>
                      {/* Up to two items named, the rest collapsed. Full list in the drawer.
                          `title` gives the whole basket on hover without widening the column. */}
                      <td className="px-4 py-3" title={itemLines(sale).map(describeItem).join(", ")}>
                        {productSummary(sale)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{sale.quantity}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        €{parseFloat(sale.totalAmount || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end">
                          <PayoutCell sale={sale} />
                        </div>
                      </td>
                      <td className={`px-4 py-3 ${statusClass(sale.paymentStatus)}`}>
                        {STATUS_LABELS[sale.paymentStatus] || sale.paymentStatus}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <TwMoreOptions
                            options={[
                              <TwListMoreButton
                                className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none"
                                onClick={() => setDetailsSale(sale)}
                              >
                                {t("onsiteSales.viewDetails", "View details")}
                              </TwListMoreButton>,
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : isEmpty ? (
            <div className="flex flex-col justify-center items-center gap-5 text-center h-full">
              <img className="w-40 md:w-60" src={noEventImage} alt="" />
              <div className="text-secondary text-lg font-bold mt-3">
                {t("onsiteSales.noSales", "No on-site sales yet")}
              </div>
              <p className="text-sm text-gray-500 max-w-sm">
                {t(
                  "onsiteSales.noSalesHelper",
                  "Sales appear here once someone scans one of your product QR codes and pays."
                )}
              </p>
            </div>
          ) : null}

          <OnsiteSaleDetailsDrawer
            open={!!detailsSale}
            sale={detailsSale}
            handleClose={() => {
              setDetailsSale(null);
              // Refresh so a just-sent receipt or refreshed figures show immediately.
              load();
            }}
          />
    </div>
  );
}
